/* "the way": original pixel concept art, rendered in code.
   A lone pilgrim with a lantern walks a narrow path through an ancient forest toward a ruined
   kingdom, where a distant light breaks the dark. Rendered into a small pixel buffer and scaled
   by a whole number so every pixel stays crisp. Retro 12fps, paused off-screen, one still frame
   under reduced motion. As the section scrolls in, the scene resolves from mosaic blocks into
   crisp pixels and warms from the site's space blue into lantern light (the portal).
   Every sprite, tile, and shape here is original. */
(() => {
  const reducedQ = window.matchMedia('(prefers-reduced-motion: reduce)');

  // ---------- palette ----------
  const hex = (h) => { const n = parseInt(h.slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
  const P = {
    sky0: '#03050a', sky1: '#05080f', sky2: '#0a1020', sky3: '#111a33', sky4: '#1a2547', sky5: '#283258',
    dusk1: '#3a2c48', dusk2: '#6a3c44', dusk3: '#b0602f', lamp: '#ffb23e', lampHot: '#ffd37a', core: '#fff3d0',
    mtn1: '#0d1429', mtn2: '#131d39', mtnRim: '#2c3c6a', mtnRimWarm: '#6a4a52',
    hill: '#0a0f1f', ruin: '#0c1224', ruinRim: '#3a3452', ruinWin: '#ff9d2e',
    fog1: '#26304f', fog2: '#36416a',
    tree1: '#070b16', tree2: '#050810', treeRim: '#1c2440', fg: '#020409', fgRimWarm: '#4a3322',
    path1: '#171b2b', path2: '#222840', pathStone: '#2d3350', pathLit: '#5c4630', pathLitHot: '#9a7040',
    cloak: '#1f1813', cloak2: '#2d2319', cloakShade: '#0e0b08', skin: '#6e5140', staff: '#5a4630',
    lanternBody: '#3a2a18', ember: '#ffcf6a', emberHot: '#fff0b0', star: '#dfe8ff', starCool: '#9fc4ff',
  };
  const C = {}; for (const k in P) C[k] = hex(P[k]);

  // 4x4 Bayer matrix, normalised 0..1, for ordered dithering (the 16-bit look)
  const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5].map((v) => (v + 0.5) / 16);
  const bayer = (x, y) => BAYER[(y & 3) * 4 + (x & 3)];

  // deterministic noise so the composition is designed, not random per visit
  const rng = (seed) => () => { seed |= 0; seed = (seed + 0x6d2b79f5) | 0; let t = Math.imul(seed ^ (seed >>> 15), 1 | seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
  const hash1 = (n) => { const s = Math.sin(n * 127.1) * 43758.5453; return s - Math.floor(s); };
  const noise1 = (x) => { const i = Math.floor(x), f = x - i, u = f * f * (3 - 2 * f); return hash1(i) * (1 - u) + hash1(i + 1) * u; };
  const ridge = (x, o) => noise1(x * 0.9 + o) * 0.55 + noise1(x * 2.3 + o * 2) * 0.3 + noise1(x * 5.1 + o * 3) * 0.15;

  // ---------- pilgrim sprite (seen from behind, walking up the path) ----------
  // . transparent  c cloak  d cloak light  s shade  h hood edge  t staff  l lantern body  L lantern light
  // . transparent  s cloak shade  c cloak  d cloak fold light  a arm  t staff  l lantern body  L lantern flame
  const PILGRIM = [
    [
      '......sss....t.',
      '.....sccds...t.',
      '....sccccds..t.',
      '....scsscds..t.',
      '.....scccs...t.',
      '...sccccccs..t.',
      '..sccdcccccs.t.',
      '.aaccdccccccst.',
      'a..ccdccccccst.',
      'l..ccdcccccc.t.',
      'L..ccdcccccc.t.',
      'l..cccccccccst.',
      '...ccccccccc.t.',
      '..scccccccccst.',
      '..cccccccccccst',
      '..ccccc.ccccc.t',
      '...sss...sss..t',
    ],
    [
      '......sss....t.',
      '.....sccds...t.',
      '....sccccds..t.',
      '....scsscds..t.',
      '.....scccs...t.',
      '...sccccccs..t.',
      '..sccdcccccs.t.',
      '.aaccdccccccst.',
      'a..ccdccccccst.',
      'a..ccdcccccc.t.',
      'l..ccdcccccc.t.',
      'L..cccccccccst.',
      'l..ccccccccc.t.',
      '..scccccccccst.',
      '..cccccccccccst',
      '..cccccc.cccc.t',
      '....sss..sss..t',
    ],
  ];

  function Scene(host) {
    const canvas = host.querySelector('canvas');
    const ctx = canvas.getContext('2d');
    let W = 0, H = 0, S = 1, img, buf;
    let bg = null; // static layers composited once per resize
    let t0 = performance.now(), last = 0, raf = 0, visible = false, portal = 0, scrollP = 0;
    const reduced = reducedQ.matches;
    let stars = [], flies = [], torches = [];
    let light = { x: 0, y: 0 }, gate = { x: 0, y: 0 }, horizon = 0, skyMask = null;

    function resize() {
      const r = host.getBoundingClientRect();
      if (!r.width) return;
      // Whole-number scale: art pixels map to an exact block of CSS pixels
      S = Math.max(2, Math.round(r.width / (r.width < 600 ? 190 : 320)));
      W = Math.max(120, Math.floor(r.width / S));
      H = Math.max(80, Math.floor(r.height / S));
      canvas.width = W; canvas.height = H;
      canvas.style.width = `${W * S}px`; canvas.style.height = `${H * S}px`;
      img = ctx.createImageData(W, H);
      buf = new Uint8ClampedArray(W * H * 4);
      compose();
      draw(performance.now());
    }

    // ---------- pixel helpers on a plain RGBA buffer ----------
    const put = (b, x, y, c) => { if (x < 0 || y < 0 || x >= W || y >= H) return; const i = (y * W + x) * 4; b[i] = c[0]; b[i + 1] = c[1]; b[i + 2] = c[2]; b[i + 3] = 255; };
    const get = (b, x, y) => { const i = (y * W + x) * 4; return [b[i], b[i + 1], b[i + 2]]; };
    const mix = (a, c, f) => [a[0] + (c[0] - a[0]) * f, a[1] + (c[1] - a[1]) * f, a[2] + (c[2] - a[2]) * f];
    // dithered two-tone: pick c2 over c1 when the ordered threshold says so
    const dith = (b, x, y, c1, c2, f) => put(b, x, y, f > bayer(x, y) ? c2 : c1);

    function compose() {
      const b = new Uint8ClampedArray(W * H * 4);
      const R = rng(7);
      horizon = Math.round(H * 0.56);
      light = { x: Math.round(W * 0.6), y: horizon - Math.round(H * 0.012) };
      gate = { x: light.x - Math.round(W * 0.02), y: horizon + Math.round(H * 0.07) };

      // Sky: space blue at the top warming toward the light, banded with ordered dither
      const bands = [C.sky0, C.sky1, C.sky2, C.sky3, C.sky4, C.sky5, C.dusk1, C.dusk2];
      for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
          const v = y / horizon; // 0 top → 1 horizon
          const dl = Math.hypot((x - light.x) / (W * 0.55), (y - light.y) / (H * 0.5));
          let k = Math.min(bands.length - 1.001, Math.max(0, v * 4.6 + Math.max(0, 1 - dl) * 3.2));
          const i = Math.floor(k), f = k - i;
          dith(b, x, y, bands[i], bands[Math.min(bands.length - 1, i + 1)], f);
        }
      }
      // Warm halo around the light
      for (let y = 0; y < horizon + 4; y++) for (let x = 0; x < W; x++) {
        const d = Math.hypot(x - light.x, (y - light.y) * 1.6) / (W * 0.22);
        if (d < 1) { const f = (1 - d) ** 2; if (f > bayer(x, y) * 1.1) put(b, x, y, f > 0.55 ? C.dusk3 : C.dusk2); }
      }

      // Stars (dynamic twinkle is drawn per frame; positions fixed here)
      skyMask = new Uint8Array(W * H); // filled after the land layers below
      stars = [];
      const sc = Math.round(W * H / 420);
      for (let i = 0; i < sc; i++) {
        const x = Math.floor(R() * W), y = Math.floor(R() * horizon * 0.8);
        if (Math.hypot(x - light.x, y - light.y) < W * 0.2) continue;
        stars.push({ x, y, big: R() > 0.9, cool: R() > 0.6, ph: R() * 6.28, sp: 0.6 + R() * 1.6 });
      }

      // Far mountains, two ridges, rim-lit toward the light
      const ridgeFill = (base, amp, off, col, rim, rimWarm) => {
        for (let x = 0; x < W; x++) {
          const top = Math.round(base - ridge(x / W * 6, off) * amp);
          for (let y = top; y < H; y++) put(b, x, y, col);
          const toward = 1 - Math.min(1, Math.abs(x - light.x) / (W * 0.35));
          if (toward > 0.05) put(b, x, top, toward > 0.5 ? rimWarm : rim);
        }
      };
      ridgeFill(horizon + 2, H * 0.2, 3.1, C.mtn1, C.mtnRim, C.mtnRimWarm);
      ridgeFill(horizon + Math.round(H * 0.05), H * 0.12, 9.7, C.mtn2, C.mtn2, C.mtnRim);

      // The hill and the ruined kingdom beneath the light
      const hillTop = gate.y - Math.round(H * 0.04);
      for (let x = 0; x < W; x++) {
        const dx = (x - gate.x) / (W * 0.22);
        const y0 = Math.round(hillTop + dx * dx * H * 0.09);
        for (let y = y0; y < H; y++) put(b, x, y, C.hill);
      }
      const unit = Math.max(1, Math.round(W / 180));
      const ruinRect = (x, y, w, h, col = C.ruin) => { for (let yy = y; yy < y + h; yy++) for (let xx = x; xx < x + w; xx++) put(b, xx, yy, col); };
      const crenel = (x, y, w, u) => { for (let xx = x; xx < x + w; xx += u * 2) ruinRect(xx, y - u, u, u); };
      const gx = gate.x, gy = hillTop;
      // curtain wall with a broken gap and a gate arch
      ruinRect(gx - 26 * unit, gy - 7 * unit, 52 * unit, 7 * unit);
      crenel(gx - 26 * unit, gy - 7 * unit, 16 * unit, unit);
      crenel(gx + 12 * unit, gy - 7 * unit, 14 * unit, unit);
      for (let xx = gx - 4 * unit; xx < gx + 5 * unit; xx++) for (let yy = gy - 5 * unit; yy < gy; yy++) {
        const ax = (xx - gx) / (4.5 * unit), ay = (yy - (gy - 5 * unit)) / (5 * unit);
        if (ax * ax + (1 - ay) * (1 - ay) * 0.35 < 1 || ay > 0.4) put(b, xx, yy, C.hill);
      }
      // collapsed stretch
      for (let xx = gx + 5 * unit; xx < gx + 12 * unit; xx++) { const h = Math.round((1 + Math.sin(xx * 1.7)) * unit * 1.5); for (let yy = gy - 7 * unit; yy < gy - h - 2 * unit; yy++) put(b, xx, yy, C.hill); }
      // towers: tall keep, broken tower, small tower
      ruinRect(gx - 22 * unit, gy - 20 * unit, 7 * unit, 13 * unit); crenel(gx - 22 * unit, gy - 20 * unit, 7 * unit, unit);
      ruinRect(gx + 16 * unit, gy - 14 * unit, 6 * unit, 7 * unit); crenel(gx + 16 * unit, gy - 14 * unit, 6 * unit, unit);
      ruinRect(gx - 8 * unit, gy - 27 * unit, 9 * unit, 20 * unit);
      // the broken keep top: a jagged, fallen crown
      for (let xx = gx - 8 * unit; xx < gx + unit; xx++) { const jag = Math.round((0.5 + 0.5 * Math.sin(xx * 2.3) * Math.cos(xx * 0.7)) * 4 * unit); for (let yy = gy - 27 * unit; yy < gy - 27 * unit + jag; yy++) put(b, xx, yy, bayer(xx, yy) > 0.35 ? C.hill : get(b, xx, yy)); }
      // rim light where stone faces the dawn: top edges and the side toward the light, read from a snapshot
      const snap = b.slice();
      const isRuin = (x, y) => { const i = (y * W + x) * 4; return snap[i] === C.ruin[0] && snap[i + 1] === C.ruin[1] && snap[i + 2] === C.ruin[2]; };
      for (let y = gy - 30 * unit; y < gy; y++) for (let x = gx - 28 * unit; x < gx + 28 * unit; x++) {
        if (y < 1 || x < 1 || x >= W - 1 || !isRuin(x, y)) continue;
        const top = !isRuin(x, y - 1), side = x > light.x ? !isRuin(x - 1, y) : !isRuin(x + 1, y);
        if (top || (side && bayer(x, y) > 0.3)) put(b, x, y, C.ruinRim);
      }
      // a few windows still holding a light, and torch positions
      [[gx - 4 * unit, gy - 20 * unit], [gx - 19 * unit, gy - 15 * unit], [gx + 18 * unit, gy - 11 * unit]].forEach(([x, y]) => { ruinRect(x, y, unit, 2 * unit, C.ruinWin); });
      torches = [{ x: gx - 6 * unit, y: gy - 2 * unit }, { x: gx + 6 * unit, y: gy - 2 * unit }];
      light = { x: Math.round(gx + 8.5 * unit), y: Math.round(gy - 8.5 * unit) };

      // Mid forest: ranks of old conifers lining a corridor for the path
      const tree = (x, base, h, col, rim) => {
        const w = Math.max(3, Math.round(h * 0.42));
        for (let yy = 0; yy < h; yy++) {
          const ty = base - yy;
          const tier = (yy % Math.max(3, Math.round(h / 5)));
          const hw = Math.round((w * (1 - yy / h)) * (0.62 + 0.38 * (tier / Math.max(3, Math.round(h / 5)))));
          for (let xx = -hw; xx <= hw; xx++) put(b, x + xx, ty, col);
          if (rim && hw > 0 && yy > h * 0.3) put(b, x + hw, ty, rim);
        }
        for (let yy = 0; yy < Math.round(h * 0.12); yy++) put(b, x, base + yy, col);
      };
      const rowY = (k) => Math.round(gate.y + (H - gate.y) * k);
      for (let row = 0; row < 3; row++) {
        const k = 0.18 + row * 0.2;
        const baseY = rowY(k);
        const hTree = Math.round(H * (0.12 + row * 0.07));
        const col = row === 0 ? C.tree1 : C.tree2;
        const corridor = W * (0.07 + k * 0.26);
        const cx = pathX(k);
        for (let x = -4; x < W + 4; x += Math.max(4, Math.round(hTree * (0.34 + R() * 0.2)))) {
          if (Math.abs(x - cx) < corridor) continue;
          tree(x, baseY + Math.round(R() * 2), Math.round(hTree * (0.75 + R() * 0.5)), col, row === 0 ? C.treeRim : null);
        }
      }

      // The narrow path: winding from the viewer up to the gate
      for (let y = gate.y; y < H; y++) {
        const k = (y - gate.y) / (H - gate.y);
        const cx = pathX(k), hw = 0.6 + Math.pow(k, 1.5) * W * 0.2;
        for (let x = Math.floor(cx - hw); x <= Math.ceil(cx + hw); x++) {
          const edge = Math.abs(x - cx) / hw;
          const c = edge > 0.82 ? C.path1 : C.path2;
          put(b, x, y, c);
          if (hash1(x * 13.1 + y * 7.7) > 0.93 - k * 0.05) put(b, x, y, C.pathStone);
        }
      }
      // Foreground trees framing the scene, gnarled branches reaching in
      const fgTrunk = (x0, dir) => {
        const tw = Math.round(W * 0.075);
        for (let y = 0; y < H; y++) {
          const wob = Math.round(Math.sin(y * 0.09 + x0) * tw * 0.18 + (y / H) * tw * 0.3 * dir);
          for (let x = 0; x < tw + Math.round((y / H) * tw * 0.4); x++) put(b, dir > 0 ? x0 + x + wob : x0 - x + wob, y, C.fg);
        }
        for (let n = 0; n < 4; n++) {
          let bx = dir > 0 ? x0 + tw : x0 - tw, by = Math.round(H * (0.08 + n * 0.14));
          for (let s = 0; s < W * (0.12 + R() * 0.1); s++) {
            bx += dir; by += Math.round(Math.sin(s * 0.3 + n) * 0.8 - 0.25);
            const th = Math.max(1, Math.round(3 - s / 14));
            for (let q = 0; q < th; q++) put(b, bx, by + q, C.fg);
            if (s % 7 === 3) for (let q = 1; q < 5; q++) put(b, bx + dir * q, by - q, C.fg);
          }
        }
      };
      fgTrunk(-Math.round(W * 0.02), 1);
      fgTrunk(W + Math.round(W * 0.02), -1);

      // Everything still showing sky colours is open sky: the light and its rays may only paint there
      for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
        const c = get(b, x, y);
        const sky = [C.sky0, C.sky1, C.sky2, C.sky3, C.sky4, C.sky5, C.dusk1, C.dusk2, C.dusk3].some((k) => k[0] === c[0] && k[1] === c[1] && k[2] === c[2]);
        skyMask[y * W + x] = sky ? 1 : 0;
      }

      // Fireflies drift through the corridor
      flies = [];
      for (let i = 0; i < Math.round(W / 18); i++) flies.push({ x: W * (0.2 + R() * 0.6), y: gate.y + (H - gate.y) * (0.1 + R() * 0.75), ph: R() * 6.28, sp: 0.4 + R() * 0.8, r: 3 + R() * 7 });

      bg = b;
    }

    function pathX(k) { return gate.x + (W * 0.5 - gate.x) * k + Math.sin(k * Math.PI * 1.35) * W * 0.12 * (1 - k * 0.4); }

    function draw(now) {
      if (!bg) return;
      const t = reduced ? 3.2 : (now - t0) / 1000;
      buf.set(bg);
      const b = buf;

      // twinkling stars; brighter ones as pixel crosses
      stars.forEach((s) => {
        const on = reduced ? 1 : 0.55 + 0.45 * Math.sin(t * s.sp + s.ph);
        if (on < 0.25) return;
        const c = s.cool ? C.starCool : C.star;
        put(b, s.x, s.y, on > 0.7 ? c : C.sky4);
        if (s.big && on > 0.6) { put(b, s.x + 1, s.y, C.sky5); put(b, s.x - 1, s.y, C.sky5); put(b, s.x, s.y + 1, C.sky5); put(b, s.x, s.y - 1, C.sky5); }
      });

      // the distant light: a steady core and slow-breathing rays
      const pulse = reduced ? 0.8 : 0.72 + 0.28 * Math.sin(t * 0.8);
      const rays = 9;
      for (let r = 0; r < rays; r++) {
        const a = -Math.PI / 2 + (r - (rays - 1) / 2) * 0.2;
        const len = H * (0.18 + (r % 3) * 0.07) * pulse;
        for (let d = 3; d < len; d++) {
          const x = Math.round(light.x + Math.cos(a) * d), y = Math.round(light.y + Math.sin(a) * d);
          const f = (1 - d / len) * 0.9;
          if (f > bayer(x, y) && skyMask[y * W + x]) put(b, x, y, f > 0.55 ? C.lampHot : C.dusk3);
        }
      }
      // light spilling over the rubble: stone edges beneath open sky near the source catch gold
      const RR = Math.round(W * 0.07);
      for (let y = Math.max(1, light.y - RR); y < Math.min(H, light.y + RR); y++) for (let x = Math.max(0, light.x - RR); x < Math.min(W, light.x + RR); x++) {
        if (skyMask[y * W + x] || !skyMask[(y - 1) * W + x]) continue;
        const d = Math.hypot(x - light.x, (y - light.y) * 1.4) / RR;
        if (d < 1) put(b, x, y, d < 0.45 ? C.lampHot : d < 0.8 ? C.lamp : C.dusk3);
      }
      for (let y = -4; y <= 4; y++) for (let x = -4; x <= 4; x++) { const d = Math.hypot(x, y), X = light.x + x, Y = light.y + y; if (d <= 4.2 && X >= 0 && Y >= 0 && X < W && Y < H && skyMask[Y * W + X]) put(b, X, Y, d < 1.8 ? C.core : d < 3 ? C.lampHot : C.lamp); }

      // fog: two dithered banks drifting at their own speeds
      const fog = (y0, h, speed, col, dens) => {
        for (let y = y0; y < y0 + h; y++) {
          const vy = 1 - Math.abs((y - y0 - h / 2) / (h / 2));
          for (let x = 0; x < W; x++) {
            const u = (x + t * speed) * 0.03;
            const bank = Math.max(0, noise1(x * 0.012 + t * speed * 0.002 + y0) * 1.6 - 0.45); // separate banks with gaps
            const n = (noise1(u + y * 0.21) * 0.6 + noise1(u * 2.7 - y * 0.37 + 11) * 0.4) * vy * vy * bank;
            if (n * dens > bayer(x, y) * 0.9 + 0.32) put(b, x, y, col);
          }
        }
      };
      fog(horizon + Math.round(H * 0.005), Math.round(H * 0.13), 4, C.fog1, 1.5);

      // torches on the ruin gate
      torches.forEach((q, i) => {
        const f = reduced ? 1 : (Math.sin(t * 9 + i * 2) + Math.sin(t * 13.7 + i)) * 0.5;
        put(b, q.x, q.y, C.lamp); put(b, q.x, q.y - 1, f > 0 ? C.lampHot : C.lamp); if (f > 0.3) put(b, q.x, q.y - 2, C.core);
      });

      // the pilgrim walks slowly up the path
      const walk = reduced ? 0 : (t * 0.012) % 0.08;
      const k = 0.6 - walk;
      const py = Math.round(gate.y + (H - gate.y) * k);
      const px = Math.round(pathX(k));
      const frame = reduced ? 0 : Math.floor(t * 3) % 2;
      const spr = PILGRIM[frame];
      const sp = Math.max(1, Math.round(110 / (spr.length * S))); // sprite pixel size in art pixels (~110 CSS px tall)
      const sh = spr.length, sw = spr[0].length;
      const ox = px - Math.round((sw * sp) / 2), oy = py - sh * sp;
      const lanternRow = spr.findIndex((r) => r[0] === 'L');

      // lantern light: a flickering warm pool on path, trees, and fog
      const lx = ox + Math.round(sp / 2), ly = oy + lanternRow * sp + Math.round(sp / 2);
      const flick = reduced ? 1 : 0.9 + 0.1 * Math.sin(t * 11) + 0.05 * Math.sin(t * 23.3);
      const LR = Math.max(14, W * 0.1) * flick;
      for (let y = Math.max(0, Math.floor(ly - LR)); y < Math.min(H, ly + LR); y++) for (let x = Math.max(0, Math.floor(lx - LR)); x < Math.min(W, lx + LR); x++) {
        const d = Math.hypot(x - lx, (y - ly) * 1.25) / LR;
        if (d >= 1) continue;
        const f = (1 - d) * (1 - d);
        const c = get(b, x, y);
        const isPath = c[0] === C.path2[0] || c[0] === C.path1[0] || c[0] === C.pathStone[0];
        const warm = isPath ? (f > 0.45 ? C.pathLitHot : C.pathLit) : (f > 0.5 ? C.fgRimWarm : C.cloak2);
        if (f * 1.6 > bayer(x, y) + 0.05) put(b, x, y, isPath || f > 0.3 ? warm : mix(c, warm, 0.55));
      }
      // sprite, drawn in sp×sp blocks, with a warm rim on the lantern side and a cool rim on the hood
      const at = (x, y) => (y >= 0 && y < sh && x >= 0 && x < sw ? spr[y][x] : '.');
      const body = (ch) => ch === 'c' || ch === 'd' || ch === 's';
      const block = (x, y, col) => { for (let yy = 0; yy < sp; yy++) for (let xx = 0; xx < sp; xx++) put(b, ox + x * sp + xx, oy + y * sp + yy, col); };
      for (let y = 0; y < sh; y++) for (let x = 0; x < sw; x++) {
        const ch = at(x, y);
        if (ch === '.') continue;
        let col = { c: C.cloak, d: C.cloak2, s: C.cloakShade, a: C.cloak2, t: C.staff, l: C.lanternBody, L: C.lampHot }[ch] || C.cloak;
        if (body(ch) && !body(at(x - 1, y)) && at(x - 1, y) !== 'a') col = y > 5 ? C.pathLitHot : C.pathLit;   // lantern-side rim
        else if (body(ch) && !body(at(x, y - 1))) col = C.treeRim;                                               // hood and shoulders catch the far light
        block(x, y, col);
      }
      block(sw - 2, -1, C.pathLit); // staff tip catches the light
      put(b, lx, ly, C.core);

      // fireflies
      flies.forEach((f, i) => {
        const x = Math.round(f.x + Math.sin(t * f.sp + f.ph) * f.r), y = Math.round(f.y + Math.cos(t * f.sp * 0.7 + f.ph) * f.r * 0.6);
        const on = reduced ? (i % 3 === 0 ? 1 : 0) : 0.5 + 0.5 * Math.sin(t * 2.2 + f.ph * 3);
        if (on > 0.55) put(b, x, y, on > 0.85 ? C.emberHot : C.ember);
      });

      // near fog curling over the path
      fog(H - Math.round(H * 0.22), Math.round(H * 0.2), -6, C.fog2, 1.25);

      // Portal: before the section is fully in, the scene resolves out of mosaic blocks and cold space
      const p = portal;
      if (p < 0.999) {
        const block = Math.max(1, Math.round((1 - p) * 10));
        const cold = C.sky1;
        for (let y = 0; y < H; y += block) for (let x = 0; x < W; x += block) {
          let c = get(b, Math.min(W - 1, x + (block >> 1)), Math.min(H - 1, y + (block >> 1)));
          c = mix(c, cold, (1 - p) * 0.75);
          for (let yy = y; yy < Math.min(H, y + block); yy++) for (let xx = x; xx < Math.min(W, x + block); xx++) put(b, xx, yy, c);
        }
      }

      img.data.set(b);
      ctx.putImageData(img, 0, 0);
    }

    function loop(now) {
      raf = 0;
      if (!visible || document.hidden) return;
      if (now - last >= 83) { last = now; draw(now); } // ~12fps, the rhythm of the era
      raf = requestAnimationFrame(loop);
    }
    const kick = () => { if (!raf && visible && !reduced) raf = requestAnimationFrame(loop); };

    // Portal progress: 0 as the section's top enters the viewport, 1 once the art is well in view
    const section = host.closest('[data-mc-way-section]') || host;
    // The art's document position is cached (re-measured on resize and as it nears the screen), so scrolling
    // the rest of the page never reads layout here; the portal only tracks scroll while it is near the screen.
    let top = 0, height = 1;
    const measure = () => { const r = host.getBoundingClientRect(); top = r.top + window.scrollY; height = r.height; };
    function sync() {
      const vh = window.innerHeight;
      const p = Math.min(1, Math.max(0, (vh - (top - window.scrollY)) / (Math.min(vh, height) * 0.8)));
      if (Math.abs(p - portal) > 0.005) { portal = reduced ? 1 : p; section.style.setProperty('--way-p', portal.toFixed(3)); if (reduced) draw(performance.now()); }
    }
    const onScroll = () => { if (visible) sync(); };

    new ResizeObserver(() => { resize(); measure(); }).observe(host);
    window.addEventListener('resize', () => { measure(); sync(); });
    new IntersectionObserver((e) => { visible = e[e.length - 1].isIntersecting; measure(); sync(); kick(); }, { rootMargin: '120px' }).observe(host);
    document.addEventListener('visibilitychange', kick);
    window.addEventListener('scroll', onScroll, { passive: true });
    measure();
    portal = -1;
    sync();
    resize();
  }

  // ---------- teaser: click to play, never autoplay ----------
  // The poster is a button; pressing it builds the video (so nothing heavy loads before then) and plays it
  // inside the same gesture, so sound is allowed and iPhone plays it inline. Leaving the screen pauses it.
  function Teaser(fig) {
    const screen = fig.querySelector('.mc-way__screen');
    const btn = fig.querySelector('.mc-way__play');
    const tpl = fig.querySelector('template[data-mc-teaser-sources]');
    if (!screen || !btn || !tpl) return;
    let video = null;
    btn.addEventListener('click', () => {
      if (video) return;
      video = document.createElement('video');
      video.className = 'mc-way__video';
      video.controls = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.preload = 'auto';
      video.setAttribute('aria-label', fig.dataset.videoLabel || btn.getAttribute('aria-label'));
      const img = btn.querySelector('img');
      if (img) video.poster = img.currentSrc || img.src;
      video.append(tpl.content.cloneNode(true));
      screen.append(video);
      fig.classList.add('is-playing');
      btn.hidden = true;
      // the second click of a double-click would land on the new video and pause it
      video.style.pointerEvents = 'none';
      setTimeout(() => { video.style.pointerEvents = ''; }, 400);
      // keep focus on the player (Safari doesn't focus a <video> by default; the button is gone)
      video.focus({ preventScroll: true });
      if (document.activeElement !== video) { video.tabIndex = 0; video.focus({ preventScroll: true }); }
      const played = video.play();
      if (played) played.catch(() => {}); // if the browser declines, the native controls are already there
    });
    new IntersectionObserver((entries) => {
      const e = entries[entries.length - 1];
      if (!video || video.paused || e.intersectionRatio >= 0.25) return;
      // fullscreen and picture-in-picture are the visitor's choice to keep watching while the page moves
      if (document.fullscreenElement || document.webkitFullscreenElement || document.pictureInPictureElement === video
        || video.webkitDisplayingFullscreen || video.webkitPresentationMode === 'picture-in-picture') return;
      video.pause();
    }, { threshold: [0, 0.25] }).observe(screen);
  }

  function init() {
    document.querySelectorAll('[data-mc-way]').forEach((h) => { if (!h.__mc) { h.__mc = 1; Scene(h); } });
    document.querySelectorAll('[data-mc-teaser]').forEach((f) => { if (!f.__mc) { f.__mc = 1; Teaser(f); } });
  }
  window.McWay = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
